import { getLocalStorage, setLocalStorage } from '@pharmsnap/shared/src/utils/localStorage'
import { computed, onBeforeUnmount, onMounted, ref } from '@vue/composition-api'

const MIN_DIALOG_WIDTH = 500
const MIN_EXPANDED_DIALOG_WIDTH = 800
const EXPANDED_DIALOG_WIDTH_RATIO = 0.8
const COPILOT_DIALOG_WIDTH_STORAGE_KEY = 'copilot-dialog-width'
const COPILOT_EXPANDED_DIALOG_WIDTH_STORAGE_KEY = 'copilot-expanded-dialog-width'

function getMaxDialogWidth() {
  return typeof window === 'undefined' ? MIN_DIALOG_WIDTH : window.innerWidth || MIN_DIALOG_WIDTH
}

function clampDialogWidth(width: number, minWidth = MIN_DIALOG_WIDTH) {
  return Math.min(Math.max(width, minWidth), getMaxDialogWidth())
}

function getStoredDialogWidth(key = COPILOT_DIALOG_WIDTH_STORAGE_KEY, defaultWidth = MIN_DIALOG_WIDTH, minWidth = MIN_DIALOG_WIDTH) {
  if (typeof window === 'undefined') return minWidth

  const storedWidth = getLocalStorage<number>(key) || 0
  return Number.isFinite(storedWidth) && storedWidth > 0 ? clampDialogWidth(storedWidth, minWidth) : clampDialogWidth(defaultWidth, minWidth)
}

function saveStoredDialogWidth(width: number, key = COPILOT_DIALOG_WIDTH_STORAGE_KEY, minWidth = MIN_DIALOG_WIDTH) {
  if (typeof window === 'undefined') return

  setLocalStorage(key, JSON.stringify(clampDialogWidth(width, minWidth)))
}

export function useCopilotSidebarResize() {
  const dialogWidth = ref(MIN_DIALOG_WIDTH)
  const isDragging = ref(false)
  const isExpanded = ref(false)
  const dragStartX = ref(0)
  const dragStartWidth = ref(MIN_DIALOG_WIDTH)

  const dialogWidthStyle = computed(() => `${dialogWidth.value}px`)
  const minDialogWidth = computed(() => (isExpanded.value ? MIN_EXPANDED_DIALOG_WIDTH : MIN_DIALOG_WIDTH))

  function setExpanded(expanded: boolean) {
    isExpanded.value = expanded
    dialogWidth.value = expanded
      ? getStoredDialogWidth(COPILOT_EXPANDED_DIALOG_WIDTH_STORAGE_KEY, getMaxDialogWidth() * EXPANDED_DIALOG_WIDTH_RATIO, MIN_EXPANDED_DIALOG_WIDTH)
      : getStoredDialogWidth()
  }

  function updateDialogWidth(width: number) {
    dialogWidth.value = clampDialogWidth(width, minDialogWidth.value)
  }

  function handleResizeMouseMove(event: MouseEvent) {
    if (!isDragging.value) return
    // 鼠标在页面外松开时不会触发 overlay 的 mouseup；回到页面后用 buttons 状态兜底结束拖拽。
    if (event.buttons === 0) {
      handleResizeMouseUp()
      return
    }

    updateDialogWidth(dragStartWidth.value + dragStartX.value - event.clientX)
  }

  function handleResizeMouseUp() {
    if (!isDragging.value) return
    isDragging.value = false
    saveStoredDialogWidth(
      dialogWidth.value,
      isExpanded.value ? COPILOT_EXPANDED_DIALOG_WIDTH_STORAGE_KEY : COPILOT_DIALOG_WIDTH_STORAGE_KEY,
      minDialogWidth.value
    )
    document.body.style.userSelect = ''
    window.removeEventListener('blur', handleResizeMouseUp)
  }

  function handleResizeMouseDown(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    isDragging.value = true
    dragStartX.value = event.clientX
    dragStartWidth.value = dialogWidth.value
    document.body.style.userSelect = 'none'
    window.addEventListener('blur', handleResizeMouseUp)
  }

  function handleWindowResize() {
    updateDialogWidth(dialogWidth.value)
  }

  onMounted(() => {
    dialogWidth.value = getStoredDialogWidth()
    window.addEventListener('resize', handleWindowResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleWindowResize)
    handleResizeMouseUp()
  })

  return {
    dialogWidthStyle,
    isDragging,
    isExpanded,
    setExpanded,
    handleResizeMouseDown,
    handleResizeMouseMove,
    handleResizeMouseUp,
  }
}
